import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { Button } from '@material-ui/core';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { SORT_ASC, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useFindById, useTags } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';

import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import { NgpTagsField } from '../../form/NgpTagsField';
import ListFormField from '../../form/ListFormField';
import IpScopeForm from '../../ip-form/IpScopeForm';
import { useMatchingDeviceLoader, useSiteHelperService } from '../sites/SiteAdminHelper';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import { IP_RULE_FIELD } from '../../ip-form/IpFormConstants';
import { businessServiceFilterId } from '../../filters';
import { BUSINESS_SERVICE_TYPE } from '../../matrix/MatrixConfigHelper';
import {
    DESCRIPTION_FIELD,
    IP_RULES_FIELD,
    NAME_FIELD,
    notificationsEmailDataSource,
    RULES_COLUMNS,
    SERVICE_TESTS_FIELD,
    TAGS_FIELD,
} from './BusinessServiceAdminConstants';
import {
    MATCHING_DEVICE_DATA_SOURCE,
    MATCHING_DEVICES_COLUMNS,
    NORM_NAME_FIELD,
    NOTIFICATIONS_COLUMNS,
} from '../sites/SiteAdminConstants';
import { BusinessServicesAdminTests } from './BusinessServicesAdminTests';
import { throwValidationError } from '../../../services/ApiDataService';
import LinkButton from '../../links/LinkButton';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';

const functionName = AdminPages.BusinessServices;
const goToUrl = '../';
const apiUrl = '/ipm/v1/admin/business-services/';
const apiTagsUrl = '/ipm/v1/admin/business-services/tags';
const defaultValues = { [TAGS_FIELD]: [] };

const reportingLinkConfig = {
    target: 'business-service-detail',
    mappedFilterParams: {
        [businessServiceFilterId]: BUSINESS_SERVICE_TYPE,
    },
};

const emptyRulesGridText = t`No rules defined`;
const addButtonText = t`Add IP Scope`;
const emptyDevicesGridText = t`There are no devices provisioned in this business service`;
const defaultVisibleRows = 12;
const initialSortRules = [{ property: IP_RULE_FIELD, direction: SORT_ASC }];
const initialSortMatchingDevice = [{ property: NORM_NAME_FIELD, direction: SORT_ASC }];
const notificationsLocation = '/admin/notifications/businessService';

function prepareSavedData(dataToSave) {
    if (!_.isEmpty(dataToSave[TAGS_FIELD])) {
        _.set(dataToSave, TAGS_FIELD, _.flatten(dataToSave[TAGS_FIELD]));
    }

    return dataToSave;
}

const selectSomethingErrorText = t`Associated Service Tests or IP Scopes are required.`;

export default function BusinessServiceAdminDetail() {
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, setError, getValues, handleSubmit, watch } = methods;
    const functionBreadCrumbs = [{ link: goToUrl, text: t`Business Services` }];
    const newBreadCrumbText = t`New Business Service`;
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const rules = watch(IP_RULES_FIELD);
    const matchingDevicesLoader = useMatchingDeviceLoader(MATCHING_DEVICE_DATA_SOURCE, rules);
    const notificationsLoader = useQueryLoader(notificationsEmailDataSource(recordId), { disableFilterState: true });
    const [testSelectionIsDirty, setTestSelectionIsDirty] = useState(false);
    const [rulesSelectionIsDirty, setRulesSelectionIsDirty] = useState(false);
    const { tags: bsTags } = useTags({ url: apiTagsUrl });
    const siteHelperService = useSiteHelperService();
    const formDeleteService = useFormDeleteService();
    const formSaveService = useFormSaveService();
    const [isIpmDisabled] = useIsIpmDisabled();

    const postFindByIdHandler = (businessService) => {
        setShowDeleteButton(true);
        businessService[TAGS_FIELD] = _.map(businessService[TAGS_FIELD], (tag) => [tag]);

        return siteHelperService.queryRuleMatching(businessService[IP_RULES_FIELD]).then(() => businessService);
    };

    const { initialized, finding, formData } = useFindById({
        url: apiUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
    });

    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, NAME_FIELD, newBreadCrumbText, {
        reportingLinkConfig,
    });

    const handleFormSubmit = (dataToSave = {}) => {
        if (_.isEmpty(getValues(IP_RULES_FIELD)) && _.isEmpty(getValues(SERVICE_TESTS_FIELD))) {
            return throwValidationError(selectSomethingErrorText);
        }

        return formSaveService.save({
            url: apiUrl,
            recordId: formData._id,
            dataToSave: _.extend(prepareSavedData(dataToSave), {
                _id: formData._id,
            }),
            setError,
        });
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(apiUrl, recordId),
            onDeleteGoTo: goToUrl,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const dirtyCheck = (isDirty) => isDirty || rulesSelectionIsDirty || testSelectionIsDirty;

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {showDeleteButton && (
                        <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    )}
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                    isDirtyFunc={dirtyCheck}
                >
                    <FormSection title={<Trans>Business Service Detail</Trans>}>
                        <NgpTextField
                            fieldName={NAME_FIELD}
                            label={<Trans>Name</Trans>}
                            required
                            inputProps={{ size: 40, maxLength: 128 }}
                        />

                        <NgpTextarea
                            fieldName={DESCRIPTION_FIELD}
                            label={<Trans>Description</Trans>}
                            cols={50}
                            inputProps={{ maxLength: 225 }}
                        />

                        <NgpTagsField fieldName={TAGS_FIELD} label={<Trans>Tags</Trans>} options={bsTags} />
                    </FormSection>

                    <BusinessServicesAdminTests
                        formData={formData}
                        setAgentSelectionIsDirty={setTestSelectionIsDirty}
                    />

                    {!isIpmDisabled && (
                        <FormSection title={t`IP Scopes`} addSpacer>
                            <ListFormField
                                fieldName={IP_RULES_FIELD}
                                dbSelected={_.get(formData, IP_RULES_FIELD)}
                                addButtonText={addButtonText}
                                gridColumns={RULES_COLUMNS}
                                emptyGridText={emptyRulesGridText}
                                formComponent={<IpScopeForm />}
                                postConfirmHandler={siteHelperService.queryRuleMatching}
                                selectedListSorter={initialSortRules}
                                setSelectionIsDirty={setRulesSelectionIsDirty}
                            />
                        </FormSection>
                    )}

                    {!isIpmDisabled && (
                        <FormSection title={t`Monitored Devices`} addSpacer>
                            <DataTable
                                tableId="BS Monitored Devices"
                                loader={matchingDevicesLoader}
                                columns={MATCHING_DEVICES_COLUMNS}
                                sorters={initialSortMatchingDevice}
                                disableCSVExport
                                disableColumnRemover
                                visibleRows={defaultVisibleRows}
                                emptyText={emptyDevicesGridText}
                            />
                        </FormSection>
                    )}

                    {showDeleteButton && (
                        <FormSection title={t`Notifications`} addSpacer>
                            <div>
                                <LinkButton
                                    label={<Trans>Manage All Notifications</Trans>}
                                    newTab
                                    url={notificationsLocation}
                                />
                            </div>
                            <DataTable
                                loader={notificationsLoader}
                                columns={NOTIFICATIONS_COLUMNS}
                                disableCSVExport
                                disableColumnRemover
                                visibleRows={defaultVisibleRows}
                            />
                        </FormSection>
                    )}
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
